import { assessmentNames } from "../lib/assessment";
import { useMemo } from "react";
import { FALLBACK_EVALUATION } from "../course/outline";
import { formatPercent } from "../lib/format";
import { Blocks } from "./Blocks";
import { PresetShell } from "./PresetShell";
import {
  compactBlocks,
  createAtom,
  readNumber,
  readString,
  readStringArray,
} from "./data";
import type { PresetViewProps } from "./CourseOverview";

/**
 * Ficha de una evaluación: cuánto pesa, dónde se rinde, qué cubre y con qué se
 * evalúa.
 *
 * `weight` es una **fracción** (0.45), igual que en la rúbrica del curso; el
 * porcentaje se calcula solo al mostrarlo.
 */
export function AssessmentBrief({ preset, data, emit, interactive }: PresetViewProps) {
  const code = readString(data, "code") ?? "EA";
  const label = readString(data, "label") ?? preset.title ?? "Evaluación";
  const weight = readNumber(data, "weight");
  const platform = readString(data, "platform");
  const deliverable = readString(data, "deliverable");
  const criteria = readStringArray(data, "criteria");
  const modules = readStringArray(data, "modules");

  const components = useMemo(() => {
    const base = FALLBACK_EVALUATION.components ?? [];
    if (base.length === 0) {
      return weight === null ? [] : [{ code, label, weight }];
    }
    return base;
  }, [code, label, weight]);

  const blocks = compactBlocks([
    createAtom("metric_cards", {
      meta: { title: "La evaluación de un vistazo" },
      cards: [
        {
          label: "Peso en la nota final",
          value: weight === null ? "—" : formatPercent(weight, "fraction"),
          hint: "Sobre el total del curso",
        },
        {
          label: "Plataforma",
          value: platform ?? "Blackboard",
          hint: platform === null ? "Campus del curso" : undefined,
        },
        {
          label: "Módulos que cubre",
          value: modules.length > 0 ? modules.join(", ") : "—",
          hint: modules.length > 0 ? `${modules.length} módulos` : undefined,
        },
      ],
    }),

    components.length > 0
      ? createAtom("rubric", {
          meta: { title: "Cómo se reparte la nota" },
          total_label: "Nota final del curso",
          formula: "Nota final = 20 % CL + 45 % EA + 35 % TF",
          components,
        })
      : null,

    criteria.length > 0
      ? createAtom("callout", {
          variant: "key-idea",
          title: "Con qué se evalúa",
          body: criteria.map((criterion) => `• ${criterion}`).join("\n\n"),
        })
      : null,

    deliverable
      ? createAtom("callout", {
          variant: "warning",
          title: "Qué se entrega",
          body: deliverable,
        })
      : null,
  ]);

  return (
    <PresetShell
      eyebrow={assessmentNames(code)}
      title={preset.title ?? label}
      lead={label}
      chips={[
        ...(weight !== null
          ? [{ icon: "target" as const, label: `${formatPercent(weight, "fraction")} de la nota` }]
          : []),
        ...(platform ? [{ icon: "compass" as const, label: platform }] : []),
        ...(modules.length > 0 ? [{ icon: "layers" as const, label: modules.join(" · ") }] : []),
      ]}
    >
      <Blocks blocks={blocks} emit={emit} interactive={interactive} />
    </PresetShell>
  );
}

export default AssessmentBrief;
